/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { Box } from "@mui/material";
import React, { useState, useRef, useEffect, Children, useMemo } from "react";
import columnResizeCursor from "@Assets/icons/svg/ColumnResizeCursor.svg";

type SplitLayoutProps = {
  children: React.ReactNode;
  initialSizes?: number[] | undefined; // percentages, e.g. [30, 70]
  minSizes?: number[] | undefined; // percentages, e.g. [10, 10]
  gutterWidth?: string | undefined; // e.g. "4px"
  gutterClassName?: string | undefined;
  gutterStyle?: React.CSSProperties | undefined;
  gutterBackground?: string | undefined;
  onSizesChange?: ((sizesPct: number[]) => void) | undefined; // percentages
  onDragStart?: ((e: MouseEvent | React.MouseEvent) => void) | undefined;
  onDragEnd?: (() => void) | undefined;
  showProperties?: boolean | undefined;
  className?: string | undefined;
} & React.HTMLAttributes<HTMLDivElement>;

function SplitLayout({
  children,
  initialSizes: initialSizesProp = [],
  minSizes = [],
  gutterWidth = "0.25rem",
  gutterClassName = "",
  gutterStyle = {},
  onSizesChange,
  showProperties,
  gutterBackground = "var(--surface-white)",
  className,
  onDragStart,
  onDragEnd,
  ...props
}: SplitLayoutProps) {
  const cursorHotspotX = 12;
  const cursorHotspotY = 12;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerWidthRef = useRef(0); // px, only for drag math

  const [sizesPct, setSizesPct] = useState<number[]>([]); // store percentages
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const startXRef = useRef(0); // px
  const startSizesPctRef = useRef<number[]>([]); // percentages

  const childCount = Children.count(children);
  const initialSizes = useMemo(() => initialSizesProp, [initialSizesProp]);

  // init percentages
  useEffect(() => {
    if (childCount === 0) return;

    let next: number[];
    if (initialSizes.length === childCount) {
      next = initialSizes.slice();
    } else {
      const equal = 100 / childCount;
      // `Array(n)` is `any[]` without a type argument, which is what let the `any` reach `sizesPct`
      // and, through `renderableSizes`, the pane widths below.
      next = Array<number>(childCount).fill(equal);
    }

    // normalize to avoid drifting sums (e.g., 33.33 * 3 = 99.99)
    const sum = next.reduce((a, b) => a + b, 0);
    next = next.map((v, i: number) =>
      i === childCount - 1 ? Math.max(0, 100 - next.slice(0, -1).reduce((a, b) => a + b, 0)) : (v / sum) * 100,
    );

    setSizesPct(next);
  }, [childCount, initialSizes]);

  // track container width for drag conversion
  const updateContainerWidth = () => {
    const w = containerRef.current?.clientWidth || 0;
    containerWidthRef.current = w;
  };

  useEffect(() => {
    updateContainerWidth();
    window.addEventListener("resize", updateContainerWidth);
    return () => window.removeEventListener("resize", updateContainerWidth);
  }, []);

  function onMouseMove(e: MouseEvent) {
    if (dragIndex === null) return;
    const cw = containerWidthRef.current;
    if (!cw) return;

    const dxPx = e.clientX - startXRef.current;
    const dxPct = (dxPx / cw) * 100;

    const newSizes = [...startSizesPctRef.current];
    const leftIndex = dragIndex;
    const rightIndex = dragIndex + 1;

    const leftStart = startSizesPctRef.current[leftIndex] ?? 0;
    const rightStart = startSizesPctRef.current[rightIndex] ?? 0;
    const total = leftStart + rightStart;

    let newLeft = leftStart + dxPct;
    let newRight = rightStart - dxPct;

    const minLeft = minSizes[leftIndex] ?? 0;
    const minRight = minSizes[rightIndex] ?? 0;

    // clamp
    if (newLeft < minLeft) {
      newLeft = minLeft;
      newRight = total - newLeft;
    }
    if (newRight < minRight) {
      newRight = minRight;
      newLeft = total - newRight;
    }
    if (newLeft < minLeft || newRight < minRight) return;

    // snap to 2 decimals to reduce float drift
    newSizes[leftIndex] = Math.max(0, +newLeft.toFixed(2));
    newSizes[rightIndex] = Math.max(0, +newRight.toFixed(2));

    setSizesPct(newSizes);
    onSizesChange?.(newSizes);
  }

  function onMouseUp() {
    if (dragIndex !== null) {
      onDragEnd?.();
    }
    setDragIndex(null);
    document.body.style.userSelect = "";
  }

  // drag lifecycle
  useEffect(() => {
    if (dragIndex !== null) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
    };
  }, [dragIndex]);

  function handleMouseDown(index: number, e: React.MouseEvent) {
    if (!showProperties) return;
    e.preventDefault();
    setDragIndex(index);
    startXRef.current = e.clientX;
    startSizesPctRef.current = [...sizesPct];
    onDragStart?.(e);
  }

  const renderableSizes: number[] =
    sizesPct.length === childCount ? sizesPct : Array<number>(childCount).fill(100 / Math.max(1, childCount));

  return (
    <div ref={containerRef} className={`flex w-full h-full overflow-hidden max-w-full ${className}`} {...props}>
      {Children.map(children, (child, i: number) => {
        // A rendered child carries its identity on `key`; it has no `id` field, so the previous
        // read produced `undefined` for every pane. Text/number children have no key of their own,
        // so fall back to the pane index.
        const paneKey = (React.isValidElement(child) ? child.key : null) ?? `index-${i}`;
        return (
          <React.Fragment key={`pane-${paneKey}`}>
            <div
              className={`flex-none h-full relative`}
              style={{
                width: `${Math.max(0, renderableSizes[i] || 0)}%`,
              }}
            >
              {i % 2 !== 0 && (
                <Box
                  className={`h-full group absolute top-0 inset-s-0 z-10 ${gutterClassName}`}
                  onMouseDown={(e: React.MouseEvent<Element, MouseEvent>) => handleMouseDown(Math.floor(i / 2), e)}
                  style={{
                    ...(showProperties && {
                      cursor: `url("${columnResizeCursor}") ${cursorHotspotX} ${cursorHotspotY}, ew-resize`,
                      background: gutterBackground,
                    }),
                  }}
                >
                  <div
                    key={`gutter-${paneKey}`}
                    className={`flex-none h-full transition-all duration-200 ${
                      dragIndex !== null ? "bg-(--selection)" : "group-hover:bg-(--selection)"
                    } `}
                    style={{
                      userSelect: "none",
                      width: gutterWidth,
                      ...gutterStyle,
                    }}
                  />
                </Box>
              )}
              {child}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default SplitLayout;
